import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

const CART_LOCATION_SYNC_CHECKOUT_BUTTON_SELECTOR = '.cart__ctas .cart__checkout-button';
const CART_LOCATION_SYNC_CHECKOUT_MARKER = 'cartLocationSyncWasEnabled';
const CART_LOCATION_SYNC_CHECKOUT_BOUND_MARKER = 'cartLocationSyncCheckoutBound';
const PREFERRED_STORE_PROPERTY = '_preferred_store_code';
const PREFERRED_STORE_SYNC_SOURCE = 'preferred-store-sync';

/** Reference count so overlapping syncs do not re-enable early. */
let cartLocationSyncLoadingDepth = 0;
let preferredStoreSyncPromise = null;
let preferredStoreSyncPending = false;
let latestPreferredStoreCode = '';

// preferred_store_code is a custom cart attribute that is used to store the preferred store code for the checkout context.
// It is used by the delivery routing logic to determine which store should fulfill the order.
// The value of this attribute is set based on the user's selection in the store locator or other checkout context sources.
const CHECKOUT_CONTEXT_ATTRIBUTE_KEYS = [
  'preferred_store_code', // Custom attribute for the preferred store code, Please use preferred_store_code not _preferred_store_code
  'checkout_address1',
  'checkout_address2',
  'checkout_city',
  'checkout_province_code',
  'checkout_zip',
  'checkout_country_code',
  'checkout_address_source'
];

/** @typedef {{ long_name?: string, short_name?: string, types?: string[] }} GoogleAddressComponent */

/**
 * @param {boolean} disabled
 */
function setCartCheckoutButtonsSyncDisabled(disabled) {
  const checkoutButtons = document.querySelectorAll(CART_LOCATION_SYNC_CHECKOUT_BUTTON_SELECTOR);
  checkoutButtons.forEach((button) => {
    if (!(button instanceof HTMLButtonElement)) return;

    if (disabled) {
      // Preserve original disabled state (e.g., empty cart).
      if (!button.disabled) {
        button.dataset[CART_LOCATION_SYNC_CHECKOUT_MARKER] = 'true';
      }
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      return;
    }

    if (button.dataset[CART_LOCATION_SYNC_CHECKOUT_MARKER] === 'true') {
      button.disabled = false;
      delete button.dataset[CART_LOCATION_SYNC_CHECKOUT_MARKER];
    }
    button.removeAttribute('aria-busy');
  });
}

function beginCartLocationSyncLoading() {
  cartLocationSyncLoadingDepth += 1;
  if (cartLocationSyncLoadingDepth !== 1) return;
  setCartCheckoutButtonsSyncDisabled(true);
}

function endCartLocationSyncLoading() {
  cartLocationSyncLoadingDepth = Math.max(0, cartLocationSyncLoadingDepth - 1);
  if (cartLocationSyncLoadingDepth !== 0) return;
  setCartCheckoutButtonsSyncDisabled(false);
}

/**
 * Debounce a function call.
 * @template {(...args: any[]) => any} T
 * @param {T} fn
 * @param {number} delayMs
 * @returns {(...args: Parameters<T>) => void}
 */
function debounce(fn, delayMs) {
  /** @type {number | null} */
  let timeoutId = null;
  return (...args) => {
    if (timeoutId) window.clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => fn(...args), delayMs);
  };
}

/**
 * @param {GoogleAddressComponent[]} components
 * @param {string} type
 * @param {'short_name' | 'long_name'} [field]
 * @returns {string}
 */
function getAddressComponent(components, type, field = 'short_name') {
  const component = components.find((item) => item.types?.includes(type));
  return String(component?.[field] || '').trim();
}

/**
 * @param {any} place
 * @returns {Record<string, string>}
 */
function getAddressAttributesFromSelectedPlace(place) {
  const components = Array.isArray(place?.address_components) ? place.address_components : [];
  if (components.length === 0) return {};

  const streetNumber = getAddressComponent(components, 'street_number');
  const route = getAddressComponent(components, 'route', 'long_name');
  const address1 = [streetNumber, route].filter(Boolean).join(' ').trim();
  const city =
    getAddressComponent(components, 'locality', 'long_name') ||
    getAddressComponent(components, 'postal_town', 'long_name') ||
    getAddressComponent(components, 'sublocality', 'long_name') ||
    getAddressComponent(components, 'administrative_area_level_2', 'long_name');
  const provinceCode = getAddressComponent(components, 'administrative_area_level_1');
  const zip = getAddressComponent(components, 'postal_code');
  const countryCode = getAddressComponent(components, 'country');

  const attributes = {
    checkout_address1: address1,
    checkout_city: city,
    checkout_province_code: provinceCode,
    checkout_zip: zip,
    checkout_country_code: countryCode,
    checkout_address_source: 'selected_place'
  };

  return Object.fromEntries(Object.entries(attributes).filter(([, value]) => value));
}

/**
 * @param {any} store
 * @returns {string}
 */
function getStoreCodeFromPreferredStore(store) {
  if (!store) return '';
  const code = store._apiData?.storeCode?.value ?? store.storeCode?.value ?? store.storeCode;
  return code != null && code !== '' ? String(code).trim() : '';
}

/**
 * @returns {Promise<any | null>}
 */
async function getPreferredStoreForCheckoutContext() {
  try {
    const win = /** @type {any} */ (window);
    if (typeof win.getPreferredStore === 'function') {
      return await win.getPreferredStore();
    }

    const component = /** @type {any} */ (document.querySelector('store-locator-component'));
    if (component && typeof component.loadSelectedStore === 'function') {
      return await component.loadSelectedStore();
    }
  } catch (e) {
    console.error('[cart-location-sync] Failed to read preferred store', e);
  }

  return null;
}

/**
 * @returns {Record<string, string>}
 */
function getSelectedPlaceCheckoutAttributes() {
  try {
    const raw = window.localStorage.getItem('selectedPlace');
    if (!raw) return {};
    return getAddressAttributesFromSelectedPlace(JSON.parse(raw));
  } catch (e) {
    console.error('[cart-location-sync] Failed to parse selectedPlace', e);
    return {};
  }
}

/**
 * @returns {Promise<Record<string, string>>}
 */
async function getCheckoutContextAttributes() {
  const preferredStore = await getPreferredStoreForCheckoutContext();
  const attributes = Object.fromEntries(CHECKOUT_CONTEXT_ATTRIBUTE_KEYS.map((key) => [key, '']));
  Object.assign(attributes, getSelectedPlaceCheckoutAttributes());
  const storeCode = getStoreCodeFromPreferredStore(preferredStore);

  if (storeCode) attributes.preferred_store_code = storeCode;

  return attributes;
}

async function fetchCurrentCart() {
  const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error(`Failed to load cart: ${response.status}`);
  return response.json();
}

async function updateCartAttributes(attributes) {
  const response = await fetch(Theme.routes.cart_update_url || `${window.Shopify?.routes?.root || '/'}cart/update.js`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ attributes }),
  });
  if (!response.ok) throw new Error(`Failed to sync checkout context: ${response.status}`);
}

function areCheckoutAttributesEqual(cart, attributes) {
  return CHECKOUT_CONTEXT_ATTRIBUTE_KEYS.every((key) =>
    String(cart?.attributes?.[key] || '').trim() === String(attributes[key] || '').trim()
  );
}

async function updatePreferredStoreLineProperties(cart, preferredStoreCode) {
  const changes = (Array.isArray(cart?.items) ? cart.items : [])
    .map((item, index) => ({ item, index }))
    .filter(({ item }) =>
      String(item.properties?.[PREFERRED_STORE_PROPERTY] || '').trim() !== preferredStoreCode
    )
    .sort((a, b) => b.index - a.index);
  let updatedCart = cart;

  for (const { item, index } of changes) {
    const properties = { ...(item.properties || {}) };
    if (preferredStoreCode) {
      properties[PREFERRED_STORE_PROPERTY] = preferredStoreCode;
    } else {
      delete properties[PREFERRED_STORE_PROPERTY];
    }

    const response = await fetch(Theme.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        line: index + 1,
        quantity: item.quantity,
        properties,
      }),
    });
    if (!response.ok) throw new Error(`Failed to sync preferred store property: ${response.status}`);
    updatedCart = await response.json();
    if (updatedCart.errors) throw new Error(String(updatedCart.errors));
  }

  if (changes.length > 0) {
    document.dispatchEvent(new CartUpdateEvent(updatedCart, PREFERRED_STORE_SYNC_SOURCE, {
      source: PREFERRED_STORE_SYNC_SOURCE,
      itemCount: updatedCart.item_count || 0,
    }));
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((element) => {
      if (!(element instanceof HTMLElement) || !element.dataset.sectionId) return;
      sectionRenderer.renderSection(element.dataset.sectionId, { cache: false }).catch(() => {});
    });
  }

  return updatedCart;
}

function isLatestPreferredStore(preferredStoreCode) {
  return !latestPreferredStoreCode || latestPreferredStoreCode === preferredStoreCode;
}

async function performCheckoutContextSync() {
  const attributes = await getCheckoutContextAttributes();
  const preferredStoreCode = String(attributes.preferred_store_code || '').trim();
  if (!isLatestPreferredStore(preferredStoreCode)) return null;

  const cart = await fetchCurrentCart();
  if (!isLatestPreferredStore(preferredStoreCode)) return null;

  if (!areCheckoutAttributesEqual(cart, attributes)) {
    await updateCartAttributes(attributes);
  }
  if (!isLatestPreferredStore(preferredStoreCode)) return null;

  return updatePreferredStoreLineProperties(cart, preferredStoreCode);
}

function syncCheckoutContextToCart() {
  if (preferredStoreSyncPromise) {
    preferredStoreSyncPending = true;
    return preferredStoreSyncPromise;
  }

  preferredStoreSyncPromise = (async () => {
    let result = null;
    do {
      preferredStoreSyncPending = false;
      result = await performCheckoutContextSync();
    } while (preferredStoreSyncPending);
    return result;
  })().finally(() => {
    preferredStoreSyncPromise = null;
  });
  return preferredStoreSyncPromise;
}

async function handleCheckoutClick(event) {
  const checkoutButton = event.currentTarget;
  if (!(checkoutButton instanceof HTMLButtonElement) || checkoutButton.disabled) return;

  event.preventDefault();
  event.stopImmediatePropagation();

  beginCartLocationSyncLoading();
  try {
    const cartItemsComponent = /** @type {any} */ (document.querySelector('cart-items-component'));
    await cartItemsComponent?.syncPendingCheckoutQuantities?.();
    await syncCheckoutContextToCart();
  } catch (e) {
    console.error('[cart-location-sync] Failed to sync checkout context before checkout', e);
  } finally {
    endCartLocationSyncLoading();
    window.location.href = '/checkout';
  }
}

function bindCheckoutButtons() {
  const checkoutButtons = document.querySelectorAll(CART_LOCATION_SYNC_CHECKOUT_BUTTON_SELECTOR);
  checkoutButtons.forEach((button) => {
    if (!(button instanceof HTMLButtonElement)) return;
    if (button.dataset[CART_LOCATION_SYNC_CHECKOUT_BOUND_MARKER] === 'true') return;

    button.dataset[CART_LOCATION_SYNC_CHECKOUT_BOUND_MARKER] = 'true';
    button.addEventListener('click', handleCheckoutClick, true);
  });
}

/**
 * Helper: get product handle from a line item's URL (/products/<handle> or /products/<handle>/...)
 * @param {string} url
 * @returns {string | null}
 */
function getHandleFromItemUrl(url) {
  if (!url || typeof url !== 'string') return null;
  const parts = url.split('/products/');
  if (parts.length < 2) return null;
  const after = parts[1];
  return after?.split('?')[0]?.split('/')[0] || null;
}

/**
 * Simple in-memory cache for product JSON responses, keyed by handle.
 * @type {Map<string, any>}
 */
const productJsonCache = new Map();

/**
 * Fetch product JSON from /products/<handle>.js, with basic caching.
 * @param {string} handle
 * @returns {Promise<any | null>}
 */
async function fetchProductJson(handle) {
  if (!handle) return null;
  if (productJsonCache.has(handle)) return productJsonCache.get(handle);

  try {
    const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' }
    });
    if (!res.ok) return null;
    const data = await res.json();
    productJsonCache.set(handle, data);
    return data;
  } catch {
    return null;
  }
}

/**
 * Normalize pricebook string for comparison.
 * @param {string | null | undefined} value
 * @returns {string}
 */
function normalizePricebook(value) {
  return String(value || '').trim().toLowerCase();
}

/**
 * First variant that is available, or the first variant in the list.
 * @param {any} productJson
 * @returns {any | null}
 */
function getDefaultVariant(productJson) {
  if (!productJson?.variants?.length) return null;
  return (
    productJson.variants.find((/** @type {any} */ v) => v.available !== false) ||
    productJson.variants[0] ||
    null
  );
}

/**
 * Find a variant matching the given pricebook (metafield or option/title).
 * When pricebook is missing/empty, returns the default (first) variant.
 * @param {any} productJson
 * @param {string | null | undefined} pricebook
 * @returns {any | null}
 */
function resolveVariantForLocation(productJson, pricebook) {
  if (!productJson?.variants?.length) return null;
  const normalized = normalizePricebook(pricebook);
  if (!normalized) {
    return getDefaultVariant(productJson);
  }

  const target = normalized;

  return (
    // @ts-ignore
    productJson.variants.find((variant) => {
      const vb =
        variant?.metafields?.custom?.price_book?.value ??
        variant?.metafields?.custom?.price_book;
      if (vb) {
        return normalizePricebook(vb) === target && variant.available !== false;
      }

      const byOption =
        (typeof variant.option1 === 'string' && normalizePricebook(variant.option1) === target) ||
        (typeof variant.title === 'string' && normalizePricebook(variant.title) === target);

      return byOption && variant.available !== false;
    }) || null
  );
}

/**
 * Replace a cart line item with a different variant while preserving quantity and properties.
 * @param {any} item - Line item from /cart.js
 * @param {number|string} newVariantId
 */
// Note: previous per-line replace helper removed in favor of consolidated per-product sync.

/**
 * Sync all cart items to match the given pricebook.
 * Called when the preferred store / pricebook is changed.
 * @param {string | null | undefined} pricebook - empty => default (first) variant per product
 */
async function syncCartItemsToPricebook(pricebook) {
  beginCartLocationSyncLoading();
  try {
    // Fetch current cart
    const cartRes = await fetch('/cart.js', {
      headers: { Accept: 'application/json' }
    });
    const cart = await cartRes.json();
    if (!cart?.items?.length) return cart;

    // Group by product handle: each product becomes 1 line with total qty, variant per location
    /** @type {Record<string, { items: any[]; totalQty: number; sampleItem: any }>} */
    const byHandle = {};

    for (const item of cart.items) {
      const handle = getHandleFromItemUrl(item.url || '');
      if (!handle) continue;

      if (!byHandle[handle]) {
        byHandle[handle] = { items: [], totalQty: 0, sampleItem: item };
      }
      byHandle[handle].items.push(item);
      byHandle[handle].totalQty += Number(item.quantity) || 0;
    }

    // Prefetch all product JSON in parallel to reduce total sync time.
    const handles = Object.keys(byHandle);
    const productMap = await Promise.all(
      handles.map(async (handle) => {
        const data = await fetchProductJson(handle);
        return [handle, data];
      })
    );
    /** @type {Record<string, any | null>} */
    const productJsonByHandle = Object.fromEntries(productMap);
    let cartChanged = false;

    // Process each product sequentially to avoid race conditions in Shopify cart APIs
    for (const [handle, group] of Object.entries(byHandle)) {
      try {
        const productJson = productJsonByHandle[handle] || null;
        if (!productJson) continue;

        const matchingVariant = resolveVariantForLocation(productJson, pricebook);
        if (!matchingVariant) continue;

        const targetVariantId = Number(matchingVariant.id);

        // If this product is already fully on the target variant with the correct total qty,
        // don't do any cart operations (even if it is split across multiple lines).
        const targetLines = group.items.filter((line) => Number(line.variant_id) === targetVariantId);
        const targetQtySum = targetLines.reduce(
          (sum, line) => sum + (Number(line.quantity) || 0),
          0
        );
        if (targetQtySum === group.totalQty && targetLines.length === group.items.length) {
          continue;
        }

        const totalQty = group.totalQty || 0;
        if (!totalQty) continue;

        const baseProperties = group.sampleItem?.properties || {};

        // 1) ADD FIRST: If add fails, we do NOT remove existing cart lines.
        const addRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            id: matchingVariant.id,
            quantity: totalQty,
            properties: baseProperties
          })
        });

        if (!addRes.ok) {
          // Keep old items untouched to prevent data loss.
          continue;
        }

        // 2) REMOVE SECOND: Batch-remove all previous lines for this product (any variants).
        /** @type {Record<string, number>} */
        const updates = {};
        for (const line of group.items) {
          if (!line?.key) continue;
          updates[String(line.key)] = 0;
        }

        if (Object.keys(updates).length > 0) {
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify({ updates })
          });
          cartChanged = true;
        }
      } catch (err) {
        console.warn(`[cart-location-sync] Skip product ${handle} due to sync error`, err);
        continue;
      }
    }

    if (!cartChanged) return cart;

    // Fetch final cart and dispatch a CartUpdateEvent so UI components re-render via their own logic.
    const updatedRes = await fetch('/cart.js', {
      headers: { Accept: 'application/json' }
    });
    const updatedCart = await updatedRes.json();

    // Dispatch theme-wide CartUpdateEvent so any listeners can react.
    // Include itemCount so cart-icon does not fall back to 0 and hide the bubble.
    document.dispatchEvent(
      new CartUpdateEvent(updatedCart, 'cart-location-sync', {
        source: 'cart-location-sync',
        itemCount: updatedCart?.item_count ?? 0
      })
    );

    // Explicitly re-render all cart-items-component sections (cart page + mini cart)
    const cartItemsComponents = document.querySelectorAll(
      'cart-items-component[data-section-id]'
    );
    cartItemsComponents.forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const sectionId = el.dataset.sectionId;
      if (!sectionId) return;
      sectionRenderer.renderSection(sectionId, { cache: false }).catch(() => {});
    });
  } catch (e) {
    console.warn('[cart-location-sync] Failed to sync cart items', e);
  } finally {
    endCartLocationSyncLoading();
  }
}

/**
 * Attach listeners for location/pricebook changes from header-store-locator.
 */
function setupCartLocationSync() {
  const onLocationChanged = debounce((e) => {
    const event = /** @type {CustomEvent<{ pricebook?: string | null }>} */ (e);
    syncCartItemsToPricebook(event.detail?.pricebook)
      .then(() => syncCheckoutContextToCart())
      .catch((error) => {
        console.warn('[cart-location-sync] Failed to sync checkout context', error);
      });
  }, 300);

  document.addEventListener('location:changed', (event) => {
    latestPreferredStoreCode = getStoreCodeFromPreferredStore(event.detail?.store);
    onLocationChanged(event);
  });
  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    bindCheckoutButtons();
    if (event?.detail?.data?.source === PREFERRED_STORE_SYNC_SOURCE) return;
    syncCheckoutContextToCart().catch((error) => {
      console.warn('[cart-location-sync] Failed to sync preferred store property', error);
    });
  });
  bindCheckoutButtons();
}

/** @type {any} */ (window).syncCheckoutContextToCart = syncCheckoutContextToCart;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupCartLocationSync);
} else {
  setupCartLocationSync();
}
